import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import { isRequired } from '../../utils/validation';

const TestimonialForm = ({ testimonial = null, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    text: '',
    author: '',
    relationship: 'client',
    featured: false,
    date: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (testimonial) {
      setFormData({
        text: testimonial.text || '',
        author: testimonial.author || '',
        relationship: testimonial.relationship || 'client',
        featured: testimonial.featured || false,
        date: testimonial.date ? testimonial.date.split('T')[0] : '',
      });
    }
  }, [testimonial]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!isRequired(formData.text)) {
      newErrors.text = 'Testimonial text is required';
    }

    if (!isRequired(formData.author)) {
      newErrors.author = 'Author name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Testimonial Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Testimonial Text <span className="text-red-500">*</span>
        </label>
        <textarea
          name="text"
          value={formData.text}
          onChange={handleChange}
          rows="6"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
            errors.text ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter testimonial text"
        />
        {errors.text && (
          <p className="text-red-500 text-sm mt-1">{errors.text}</p>
        )}
      </div>

      {/* Author and Relationship */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Author <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
              errors.author ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter author name"
          />
          {errors.author && (
            <p className="text-red-500 text-sm mt-1">{errors.author}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Relationship
          </label>
          <select
            name="relationship"
            value={formData.relationship}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="client">Client</option>
            <option value="collaborator">Collaborator</option>
          </select>
        </div>
      </div>

      {/* Date and Featured */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div className="flex items-center">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
              className="w-4 h-4 text-accent focus:ring-accent border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">
              Featured Testimonial
            </span>
          </label>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-4 border-t pt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading
            ? 'Saving...'
            : testimonial
            ? 'Update Testimonial'
            : 'Create Testimonial'}
        </Button>
      </div>
    </form>
  );
};

export default TestimonialForm;
